import { generateClient } from "aws-amplify/data";
import { formatAmount } from "@cpos/shared";
import type { Schema } from "amplify/data/resource";

const client = generateClient<Schema>();

/** A record resolved by an exact, indexed lookup. */
export interface LiveMatch {
  kind: "merchant" | "transaction" | "invoice";
  id: string;
  title: string;
  subtitle: string;
  /** The merchant this record belongs to; transaction routes nest under it. */
  businessId: string;
}

/**
 * Exact lookups behind the command palette.
 *
 * Every method is a single keyed read against an existing index, never a
 * scan — which is what lets them run live on each query and stay correct for
 * records created since the last index build.
 */
export interface SearchRepository {
  findByStripeAccount(id: string): Promise<LiveMatch | null>;
  findByStripeCustomer(id: string): Promise<LiveMatch | null>;
  findByStripeSubscription(id: string): Promise<LiveMatch | null>;
  findByStripeCharge(id: string): Promise<LiveMatch | null>;
  findByEmail(email: string): Promise<LiveMatch | null>;
  findBusiness(id: string): Promise<LiveMatch | null>;
  findTransaction(id: string): Promise<LiveMatch | null>;
  findInvoice(id: string): Promise<LiveMatch | null>;
}

/** Amplify generates one `listXByY` per secondary index; they are not typed. */
type IndexQuery<T> = (
  key: Record<string, string>,
  opts?: { limit?: number },
) => Promise<{ data: T[]; errors?: { message: string }[] }>;

function indexQuery<T>(model: unknown, field: string): IndexQuery<T> {
  // eslint-disable-next-line security/detect-object-injection -- field is a hardcoded Amplify query name
  return (model as Record<string, IndexQuery<T>>)[field];
}

function merchantMatch(business: {
  id: string;
  businessName?: string | null;
}): LiveMatch {
  return {
    kind: "merchant",
    id: business.id,
    title: business.businessName ?? "Unnamed business",
    subtitle: business.id,
    businessId: business.id,
  };
}

/**
 * Amounts are `cad`, `usd` or `aud`, so the currency travels with the number.
 * An unknown amount renders as "-" rather than as a confident zero.
 */
function amountLabel(
  kind: string,
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  return `${kind} ${formatAmount(amount, currency)}`;
}

export class AmplifySearchRepository implements SearchRepository {
  async findByStripeAccount(id: string): Promise<LiveMatch | null> {
    return this._businessByIndex(
      "listBusinessProfileByStripeConnectedAccountId",
      "stripeConnectedAccountId",
      id,
    );
  }

  async findByStripeCustomer(id: string): Promise<LiveMatch | null> {
    return this._businessByIndex(
      "listBusinessProfileByStripeCustomerId",
      "stripeCustomerId",
      id,
    );
  }

  async findByStripeSubscription(id: string): Promise<LiveMatch | null> {
    return this._businessByIndex(
      "listBusinessProfileByStripeSubscriptionId",
      "stripeSubscriptionId",
      id,
    );
  }

  async findByStripeCharge(id: string): Promise<LiveMatch | null> {
    const query = indexQuery<{
      id: string;
      businessId?: string | null;
      amount?: number | null;
      currency?: string | null;
    }>(client.models.Transaction, "listTransactionBySourceChargeId");
    const { data, errors } = await query({ sourceChargeId: id }, { limit: 1 });
    if (errors?.length) throw new Error(errors[0].message);
    const txn = data[0];
    if (!txn?.businessId) return null;
    return {
      kind: "transaction",
      id: txn.id,
      title: amountLabel("Transaction", txn.amount, txn.currency),
      subtitle: id,
      businessId: txn.businessId,
    };
  }

  async findByEmail(email: string): Promise<LiveMatch | null> {
    const query = indexQuery<{ businessId?: string | null }>(
      client.models.UserProfile,
      "listUserProfileByEmail",
    );
    const { data, errors } = await query({ email }, { limit: 5 });
    if (errors?.length) throw new Error(errors[0].message);
    const businessId = data.find((u) => u.businessId)?.businessId;
    if (!businessId) return null;
    const match = await this.findBusiness(businessId);
    return match ? { ...match, subtitle: email } : null;
  }

  async findBusiness(id: string): Promise<LiveMatch | null> {
    const { data, errors } = await client.models.BusinessProfile.get({ id });
    if (errors?.length) throw new Error(errors[0].message);
    return data ? merchantMatch(data) : null;
  }

  async findTransaction(id: string): Promise<LiveMatch | null> {
    const { data, errors } = await client.models.Transaction.get({ id });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data?.businessId) return null;
    return {
      kind: "transaction",
      id: data.id,
      title: amountLabel("Transaction", data.amount, data.currency),
      subtitle: id,
      businessId: data.businessId,
    };
  }

  async findInvoice(id: string): Promise<LiveMatch | null> {
    const { data, errors } = await client.models.Invoice.get({ id });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data?.businessId) return null;
    return {
      kind: "invoice",
      id: data.id,
      title: amountLabel("Invoice", data.amount, data.currency),
      subtitle: id,
      businessId: data.businessId,
    };
  }

  private async _businessByIndex(
    queryField: string,
    keyField: string,
    value: string,
  ): Promise<LiveMatch | null> {
    const query = indexQuery<{ id: string; businessName?: string | null }>(
      client.models.BusinessProfile,
      queryField,
    );
    const { data, errors } = await query({ [keyField]: value }, { limit: 1 });
    if (errors?.length) throw new Error(errors[0].message);
    const business = data[0];
    return business ? { ...merchantMatch(business), subtitle: value } : null;
  }
}
